/**
 * PurchaseOrderListReport (LISTS-01, on the universal engine). ESC's Purchase
 * Order List (Seth's screenshot), rebuilt on the SAME engine as Invoices +
 * Dispatches: pick any column, filter by any field, group (by Vendor / Buyer /
 * Status) with Σ subtotals + a grand total, save views, export CSV. Read-only PO
 * history per DEC-22 (mirror_po via v_po_list) — creating/receiving Pegasus
 * POs stays on the /purchase-orders operational screen.
 *
 * Adapt-not-copy: ESC repeats a PO once PER LINE ITEM (27470 appears twice,
 * 27523 three times). Pegasus shows ONE row per PO with the line items in the
 * peek panel — and adds the live dollar total ESC's list never showed.
 *
 * Competitor frame: ServiceTitan's PO report + QuickBooks purchase reports —
 * filter to a vendor/period, subtotal the spend, drill a PO. The grand-total +
 * per-vendor subtotals are where the engine earns its keep for purchasing.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ShoppingCart, Search, X, Check, Building2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { pagedAll } from "@/lib/pagedAll";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import { useEscPoLines } from "@/features/purchase-orders/po";
import {
  CsvButton, DateRangeControl, ViewsMenu, downloadCsv, pillSelectCls, isoDaysAgo, isoToday,
  useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters,
  type ListField, type FilterCond,
} from "./listCore";

interface Row {
  po: string;
  date: string | null;
  date_req: string | null;
  vendor_code: string | null;
  vendor_name: string | null;
  status: string | null;
  billed: boolean | null;
  order_placed: boolean | null;
  ship_name: string | null;
  buyer: string | null;
  terms: string | null;
  ship_via: string | null;
  dispatch: string | null;
  custno: string | null;
  locno: string | null;
  memo: string | null;
  total: number | null;
  line_count: number | null;
  received_lines: number | null;
}

interface Filters { from: string; to: string; q: string; vendor: string; status: string }
const DEFAULT_FILTERS = (): Filters => ({ from: isoDaysAgo(30), to: isoToday(), q: "", vendor: "", status: "" });
const COLS = "po,date,date_req,vendor_code,vendor_name,status,billed,order_placed,ship_name,buyer,terms,ship_via,dispatch,custno,locno,memo,total,line_count,received_lines";
const DISPLAY_CAP = 1500;

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (d: string | null) => { if (!d) return "—"; try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; } };
// ESC zero-pads PO numbers ("000000000027470"); show how the office says it.
const poNum = (po: string) => po.replace(/^0+/, "") || po;
const STATUS: Record<string, { label: string; cls: string }> = {
  C: { label: "Complete", cls: "bg-emerald-50 text-emerald-700" },
  O: { label: "Open", cls: "bg-amber-50 text-amber-700" },
};
const statusMeta = (s: string | null) => STATUS[s ?? ""] ?? { label: s ?? "—", cls: "bg-slate-100 text-slate-600" };
function StatusChip({ s }: { s: string | null }) {
  const m = statusMeta(s);
  return <span className={cn("inline-block rounded-full px-2 py-0.5 text-[11px] font-medium", m.cls)}>{m.label}</span>;
}

export function PurchaseOrderListReport() {
  const navigate = useNavigateWithBack();
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);
  const sort = useSort<Row>("date", "desc");

  // The PO field catalog — the office picks columns, groups by Vendor / Buyer /
  // Status, and totals Amount. Same shape Invoices + Dispatches use.
  const fields: ListField<Row>[] = useMemo(() => [
    { id: "date", type: "date", filterable: true, col: "date", label: "Date", value: (r) => r.date, render: (r) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(r.date)}</span> },
    { id: "po", type: "text", filterable: true, col: "po", label: "PO #", value: (r) => r.po, render: (r) => <span className="font-mono text-xs font-medium text-slate-700">{poNum(r.po)}</span> },
    { id: "vendor", type: "text", filterable: true, groupable: true, col: "vendor_name", label: "Vendor", value: (r) => r.vendor_name ?? r.vendor_code, render: (r) => <span className="text-sm text-slate-700">{r.vendor_name ?? r.vendor_code ?? "—"}</span> },
    // Amount + Status sit up front so they're never scrolled off behind Memo —
    // the dollar value is the headline of a PO list (Seth: "like the invoice list").
    { id: "total", numeric: true, totalable: true, type: "number", filterable: true, col: "total", label: "Amount", value: (r) => Number(r.total) || 0, render: (r) => <span className="font-medium tabular-nums text-slate-800">{money(Number(r.total) || 0)}</span> },
    { id: "status", type: "enum", filterable: true, groupable: true, col: "status", label: "Status", value: (r) => statusMeta(r.status).label, render: (r) => <StatusChip s={r.status} />, options: [{ value: "C", label: "Complete" }, { value: "O", label: "Open" }] },
    { id: "date_req", type: "date", filterable: true, col: "date_req", label: "Date Requested", value: (r) => r.date_req, render: (r) => <span className="whitespace-nowrap text-xs text-slate-500">{fmtDate(r.date_req)}</span> },
    { id: "dispatch", type: "text", filterable: true, col: "dispatch", label: "Dispatch #", value: (r) => r.dispatch, render: (r) => r.dispatch ? <span className="font-mono text-xs text-slate-600">{r.dispatch}</span> : <span className="text-slate-300">—</span> },
    { id: "buyer", type: "text", filterable: true, groupable: true, col: "buyer", label: "Buyer", value: (r) => r.buyer },
    { id: "memo", type: "text", filterable: true, col: "memo", label: "Memo", value: (r) => r.memo, render: (r) => <span className="block max-w-[260px] truncate text-xs text-slate-600">{r.memo ?? "—"}</span> },
    // Available in the Fields menu, off by default.
    { id: "terms", type: "text", filterable: true, groupable: true, col: "terms", label: "Terms", value: (r) => r.terms, defaultVisible: false },
    { id: "ship_via", type: "text", filterable: true, col: "ship_via", label: "Ship via", value: (r) => r.ship_via, defaultVisible: false },
    { id: "ship_name", type: "text", filterable: true, col: "ship_name", label: "Ship to", value: (r) => r.ship_name, defaultVisible: false },
    { id: "billed", type: "bool", filterable: true, col: "billed", label: "Billed", value: (r) => (r.billed ? "Yes" : "No"), render: (r) => r.billed ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <span className="text-slate-300">—</span>, defaultVisible: false },
    { id: "order_placed", type: "bool", filterable: true, col: "order_placed", label: "Order Placed", value: (r) => (r.order_placed ? "Yes" : "No"), render: (r) => r.order_placed ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <span className="text-slate-300">—</span>, defaultVisible: false },
    { id: "lines", numeric: true, type: "number", filterable: true, col: "line_count", label: "Lines", value: (r) => Number(r.line_count) || 0, defaultVisible: false },
    { id: "custno", type: "text", filterable: true, col: "custno", label: "Cust #", value: (r) => r.custno, defaultVisible: false },
  ], []);
  const cols = useColumns("purchase_orders", fields);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["list_pos", f.from, f.to, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Row[]> => pagedAll<Row>(() => {
      let q = supabase.from("v_po_list").select(COLS).gte("date", f.from).lte("date", f.to);
      q = applyFilters(q, conds, fields as ListField<unknown>[]);
      return q.order("date", { ascending: false }).order("po", { ascending: false });
    }),
  });
  const all = data ?? [];

  // Toolbar quick-filters narrow client-side; the any-field Filters narrow
  // server-side. Total + count reflect the full filtered set.
  const filtered = useMemo(() => {
    const ql = f.q.trim().toLowerCase();
    return all.filter((r) => {
      if (f.vendor && (r.vendor_name ?? r.vendor_code ?? "") !== f.vendor) return false;
      if (f.status && (r.status ?? "") !== f.status) return false;
      if (ql) {
        const hay = [poNum(r.po), r.po, r.vendor_name, r.memo, r.buyer, r.dispatch, r.ship_name].join(" ").toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [all, f.q, f.vendor, f.status]);

  const grandTotal = useMemo(() => filtered.reduce((s, r) => s + (Number(r.total) || 0), 0), [filtered]);
  const vendorOpts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of all) { const k = r.vendor_name ?? r.vendor_code; if (k) m.set(k, (m.get(k) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [all]);

  const shownRows = filtered.slice(0, DISPLAY_CAP);
  const setPart = (p: Partial<Filters>) => setF((prev) => ({ ...prev, ...p }));

  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((x) => x.id === id)).filter(Boolean) as ListField<Row>[];
    downloadCsv(
      `purchase_orders_${f.from}_${f.to}.csv`,
      shown.map((c) => c.label),
      filtered.map((r) => shown.map((c) => { const v = c.value(r); return v == null ? "" : String(v); })),
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {/* TOOLBAR-REFRESH — a search-forward command row + a clean scoping row
          (date range as one pill with presets, quick filters, result summary). */}
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={f.q}
              onChange={(e) => setPart({ q: e.target.value })}
              placeholder="Search PO #, vendor, memo, buyer, dispatch…"
              className="h-9 w-full rounded-lg border border-hairline bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25"
            />
          </div>
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="purchase_orders" currentFilters={f as unknown as Record<string, unknown>} onApply={(saved) => setF({ ...DEFAULT_FILTERS(), ...(saved as Partial<Filters>) })} />
          {/* Browse view → the operational screen where create/receive lives
              (Seth 2026-08-17: view-only dead-end fixed). */}
          <button
            type="button"
            onClick={() => navigate("/purchase-orders")}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-700 px-3 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
          >
            <ShoppingCart className="h-3.5 w-3.5" /> Open PO manager
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl from={f.from} to={f.to} onChange={(from, to) => setF((prev) => ({ ...prev, from, to }))} />
          <select value={f.vendor} onChange={(e) => setPart({ vendor: e.target.value })} className={pillSelectCls} title="Filter by vendor">
            <option value="">All vendors</option>
            {vendorOpts.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
          </select>
          <select value={f.status} onChange={(e) => setPart({ status: e.target.value })} className={pillSelectCls} title="Filter by status">
            <option value="">Any status</option>
            <option value="C">Complete</option>
            <option value="O">Open</option>
          </select>
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500">
            <strong className="text-slate-800">{filtered.length.toLocaleString()}</strong> POs · <strong className="text-emerald-700">{money(grandTotal)}</strong>{isFetching ? " · …" : ""}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : isError ? (
        <Card className="p-6"><p className="text-sm text-red-600">{(error as Error)?.message}</p></Card>
      ) : (
        <div className="flex items-start gap-3">
          <Card className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><ShoppingCart className="h-3.5 w-3.5" /></span>
              <span className="text-[14.5px] font-bold tracking-tight text-slate-900">Purchase Order List</span>
              <InfoTip label="How this list works">Read-only PO history: purchasing stays in ESC, and new POs are made in the PO manager. Click a row to see its line items. For spend subtotals, group by Vendor or Buyer and total Amount in the Fields menu.</InfoTip>
              <span className="ml-auto text-xs tabular-nums text-slate-500">{filtered.length.toLocaleString()} POs{filtered.length > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP.toLocaleString()}` : ""}</span>
            </div>
            <div className="max-h-[68vh] overflow-auto">
              <ListTable
                rows={shownRows}
                fields={fields}
                cols={cols}
                sort={sort}
                getKey={(r) => r.po}
                onRowClick={(r) => setSelected(r)}
                selectedKey={selected?.po ?? null}
                emptyText="No POs match — widen the dates or clear a filter."
              />
            </div>
            {filtered.length > DISPLAY_CAP && (
              <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
                The header dollar total covers all {filtered.length.toLocaleString()} matches; the table shows the first {DISPLAY_CAP.toLocaleString()} — narrow the dates for exact on-screen subtotals.
              </p>
            )}
          </Card>
          {selected && (
            <PoDetailsPanel
              row={selected}
              onClose={() => setSelected(null)}
              onQualify={selected.custno ? () => navigate(`/customers/${selected.custno}`) : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

// The PO peek panel (two-window model: row click peeks here). ESC's right-rail
// detail, modernized — header facts + the actual line items + received status.
function PoDetailsPanel({ row, onClose, onQualify }: { row: Row; onClose: () => void; onQualify?: () => void }) {
  const { data: lines, isLoading } = useEscPoLines(row.po);
  const meta: [string, string | null][] = [
    ["Date requested", row.date_req ? fmtDate(row.date_req) : null],
    ["Buyer", row.buyer],
    ["Terms", row.terms],
    ["Ship via", row.ship_via],
    ["Dispatch #", row.dispatch],
    ["Billed", row.billed ? "Yes" : null],
    ["Order placed", row.order_placed ? "Yes" : null],
  ];
  return (
    <div className="w-80 shrink-0">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="min-w-0">
            <div className="font-mono text-sm font-bold text-slate-900">PO #{poNum(row.po)}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">{fmtDate(row.date)} <StatusChip s={row.status} /></div>
          </div>
          <button onClick={onClose} title="Close" className="shrink-0 text-slate-300 transition-colors hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="font-medium">{row.vendor_name ?? row.vendor_code ?? "—"}</span>
          </div>
          <div>
            <div className="text-[11.5px] font-semibold text-slate-500">PO total</div>
            <div className="text-2xl font-bold tabular-nums text-slate-900">{money(Number(row.total) || 0)}</div>
            <div className="text-[11px] text-slate-400">{row.line_count ?? 0} line{(row.line_count ?? 0) === 1 ? "" : "s"} · {row.received_lines ?? 0} received</div>
          </div>
          {row.memo && <div className="rounded-md bg-slate-50 px-2.5 py-2 text-xs text-slate-600">{row.memo}</div>}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3">
            {meta.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <div className="text-[11.5px] font-semibold text-slate-500">{k}</div>
                <div className="truncate text-xs text-slate-700">{v}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Line items</div>
            {isLoading ? (
              <div className="h-12 animate-pulse rounded bg-slate-100" />
            ) : (lines ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">No line items recorded.</p>
            ) : (
              <ul className="space-y-1.5">
                {(lines ?? []).map((l) => {
                  const received = l.received != null && l.quan != null && Number(l.received) >= Number(l.quan) && Number(l.quan) > 0;
                  return (
                    <li key={l.counter} className="flex items-start justify-between gap-2 text-xs">
                      <div className="min-w-0">
                        <div className="truncate text-slate-700">{l.description || l.part || "—"}</div>
                        <div className="text-[10px] text-slate-400">
                          {l.part ? <span className="font-mono">{l.part}</span> : null}
                          {l.quan != null ? <> · qty {Number(l.quan)}</> : null}
                          {received ? <> · <span className="text-emerald-600">received</span></> : null}
                          {l.dispatch_no ? <> · disp {l.dispatch_no}</> : null}
                        </div>
                      </div>
                      <div className="shrink-0 tabular-nums text-slate-700">{l.amount != null ? money(Number(l.amount)) : "—"}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {onQualify && (
            <button onClick={onQualify} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
              Qualify customer
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}
